import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../../../../api";
import { Country, City } from "country-state-city";
import { useTranslation } from "react-i18next";

const initialMission = {
    natureMissionId: "",
    code: "",
    libelle: "",
    description: "",
    objetMission: "",
    dateDebut: "",
    dateFin: "",
    zoneMission: "EN_VILLE",
    pays: "Maroc",
    ville: "Casablanca",
    adresse: "",
    engagementsCollaborateur: "",
    workflow: "both",
    notesDeFraisAutoriseesIds: [],
};

export const calculateDurationDays = (dateDebut, dateFin) => {
    if (!dateDebut || !dateFin) return null;
    const start = new Date(dateDebut);
    const end = new Date(dateFin);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null;
    return Math.floor((end - start) / 86400000) + 1;
};

const MissionInlineForm = ({
    formId,
    onSubmit,
    loading,
    submitLabel = "Soumettre",
    initialMissionData = null,
    initialUrgent = false,
    initialMotif = "",
    showUrgent = true,
    showWorkflow = false,
    showNotes = true,
    showJustificatifs = true,
    showComment = true,
    children,
}) => {
    const { t } = useTranslation();
    const [mission, setMission] = useState(initialMission);
    const [urgent, setUrgent] = useState(false);
    const [motif, setMotif] = useState("");
    const [natureMissions, setNatureMissions] = useState([]);
    const [notesDeFrais, setNotesDeFrais] = useState([]);
    const initialMissionKey = useMemo(() => JSON.stringify(initialMissionData || {}), [initialMissionData]);

    const countryNameInFrench = useMemo(() => {
        const displayNames = typeof Intl.DisplayNames === "function"
            ? new Intl.DisplayNames(["fr"], { type: "region" })
            : null;
        return (country) => displayNames?.of(country.isoCode) || country.name;
    }, []);

    const countries = useMemo(
        () => Country.getAllCountries().sort((left, right) =>
            countryNameInFrench(left).localeCompare(countryNameInFrench(right), "fr")
        ),
        [countryNameInFrench]
    );

    const selectedCountryCode = useMemo(() => {
        if (!mission.pays) return "";
        const found = countries.find(
            (c) => c.name.toLowerCase() === mission.pays.toLowerCase()
                || countryNameInFrench(c).toLowerCase() === mission.pays.toLowerCase()
                || c.isoCode === mission.pays
        );
        return found ? found.isoCode : "";
    }, [mission.pays, countries]);

    const cities = useMemo(() => {
        if (!selectedCountryCode) return [];
        return City.getCitiesOfCountry(selectedCountryCode);
    }, [selectedCountryCode]);

    const headers = useMemo(() => ({
        uid: sessionStorage.getItem("uid"),
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        "X-Tenant-Id": sessionStorage.getItem("selectedCompanyId"),
        "Content-Type": "application/json",
        Accept: "application/json",
    }), []);

    useEffect(() => {
        const isActif = (item) => item && item.actif !== false && item.actif !== "false" && item.statut !== "INACTIF" && item.isActif !== false;

        axios.get(`${API_BASE_URL}/api/nature-missions/actives`, { headers })
            .then((resp) => setNatureMissions((resp.data || []).filter(isActif)))
            .catch(() => setNatureMissions([]));

        axios.get(`${API_BASE_URL}/api/notedefrais/actifs`, { headers })
            .then((resp) => setNotesDeFrais((resp.data || []).filter(isActif)))
            .catch(() => setNotesDeFrais([]));
    }, [headers]);

    useEffect(() => {
        if (!initialMissionData) return;
        const initialZone = initialMissionData.zoneMission || "EN_VILLE";
        setMission({
            ...initialMission,
            ...initialMissionData,
            zoneMission: initialZone,
            pays: initialMissionData.pays || (initialZone === "EN_VILLE" ? "Maroc" : ""),
            ville: initialMissionData.ville || (initialZone === "EN_VILLE" ? "Casablanca" : ""),
            notesDeFraisAutoriseesIds: initialMissionData.notesDeFraisAutoriseesIds || [],
        });
        setUrgent(!!initialUrgent);
        setMotif(initialMotif || "");
        setJustificatifs([]);
    }, [initialMissionKey, initialUrgent, initialMotif]);

    const durationDays = calculateDurationDays(mission.dateDebut, mission.dateFin);

    const setField = (key, value) => {
        setMission((prev) => {
            if (key === "zoneMission" && value === "EN_VILLE") {
                return { ...prev, zoneMission: value, pays: "Maroc", ville: "Casablanca" };
            }
            return { ...prev, [key]: value };
        });
    };

    const toggleNote = (id) => {
        setMission((prev) => {
            const ids = prev.notesDeFraisAutoriseesIds || [];
            return {
                ...prev,
                notesDeFraisAutoriseesIds: ids.includes(id)
                    ? ids.filter((item) => item !== id)
                    : [...ids, id],
            };
        });
    };

    const [justificatifs, setJustificatifs] = useState([]);

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files);
        setJustificatifs((prev) => [...prev, ...selected]);
    };

    const removeFile = (indexToRemove) => {
        setJustificatifs((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        onSubmit({
            motif: motif || null,
            urgent,
            justificatifs,
            ordreMission: {
                ...mission,
                natureMissionId: mission.natureMissionId ? Number(mission.natureMissionId) : null,
                code: mission.code.trim().toUpperCase(),
                libelle: mission.libelle.trim(),
                description: mission.description || null,
                objetMission: mission.objetMission || null,
                zoneMission: mission.zoneMission || "EN_VILLE",
                pays: mission.pays || null,
                ville: mission.ville || null,
                adresse: mission.adresse || null,
                engagementsCollaborateur: mission.engagementsCollaborateur || null,
                active: true,
                notesDeFraisAutoriseesIds: showNotes ? (mission.notesDeFraisAutoriseesIds || []) : null,
            },
        });
    };

    return (
        <form id={formId} onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {children}

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Ligne 1: Nature de mission, Code, Libellé */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 16 }}>
                    <div className="pcWizField">
                        <label>{t("Nature de la mission")} <span className="pcReq">*</span></label>
                        <select
                            className="pcInput"
                            value={mission.natureMissionId}
                            onChange={(e) => setField("natureMissionId", e.target.value)}
                            required
                        >
                            <option value="">{t("-- Choisir --")}</option>
                            {natureMissions.map((nature) => (
                                <option key={nature.id} value={nature.id}>{t(nature.libelle || "", { defaultValue: nature.libelle || "" })}</option>
                            ))}
                        </select>
                    </div>

                    <div className="pcWizField">
                        <label>{t("Code")} <span className="pcReq">*</span></label>
                        <input className="pcInput" value={mission.code} onChange={(e) => setField("code", e.target.value.toUpperCase())} required />
                    </div>

                    <div className="pcWizField">
                        <label>{t("Libellé")} <span className="pcReq">*</span></label>
                        <input className="pcInput" value={mission.libelle} onChange={(e) => setField("libelle", e.target.value)} required />
                    </div>
                </div>

                {/* Ligne 2: Description, Objet de la mission */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div className="pcWizField">
                        <label>{t("Description")}</label>
                        <textarea className="pcInput pcInput--textarea" rows={2} value={mission.description} onChange={(e) => setField("description", e.target.value)} />
                    </div>

                    <div className="pcWizField">
                        <label>{t("Objet de la mission")} <span className="pcReq">*</span></label>
                        <textarea className="pcInput pcInput--textarea" rows={3} value={mission.objetMission} onChange={(e) => setField("objetMission", e.target.value)} required />
                    </div>
                </div>

                {/* Ligne 3: Date début, date fin et durée totale */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                    <div className="pcWizField">
                        <label>{t("Date de départ")} <span className="pcReq">*</span></label>
                        <input type="date" className="pcInput" value={mission.dateDebut} onChange={(e) => setField("dateDebut", e.target.value)} required />
                    </div>

                    <div className="pcWizField">
                        <label>{t("Date de retour")} <span className="pcReq">*</span></label>
                        <input type="date" className="pcInput" value={mission.dateFin} onChange={(e) => setField("dateFin", e.target.value)} required />
                    </div>

                    <div className="pcWizField">
                        <label>{t("Durée totale")}</label>
                        <input className="pcInput" value={durationDays ? `${durationDays} ${t("jours")}` : ""} disabled />
                    </div>
                </div>

                {/* Ligne 4: Zone, Pays Ville Adresse */}
                <div className="pcWizField">
                    <label>{t("Zone de mission")}</label>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        {[
                            { value: "EN_VILLE", label: "En ville" },
                            { value: "HORS_VILLE", label: "Hors ville" },
                        ].map((option) => {
                            const checked = mission.zoneMission === option.value;
                            return (
                                <label
                                    key={option.value}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "10px 14px",
                                        border: `1px solid ${checked ? "#204385" : "#e2e8f0"}`,
                                        borderRadius: 8,
                                        cursor: "pointer",
                                        fontWeight: checked ? 700 : 500,
                                        color: checked ? "#204385" : "#334155",
                                        background: checked ? "#f0f4ff" : "#fff",
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name={`${formId || "mission"}-zoneMission`}
                                        value={option.value}
                                        checked={checked}
                                        onChange={(e) => setField("zoneMission", e.target.value)}
                                    />
                                    <span>{t(option.label)}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 16 }}>
                    <div className="pcWizField">
                        <label>{t("Pays")}</label>
                        <select
                            className="pcInput"
                            value={selectedCountryCode}
                            onChange={(e) => {
                                const code = e.target.value;
                                const countryObj = countries.find((c) => c.isoCode === code);
                                setMission((prev) => ({
                                    ...prev,
                                    pays: countryObj ? countryNameInFrench(countryObj) : "",
                                    ville: "",
                                }));
                            }}
                        >
                            <option value="">{t("-- Choisir un pays --")}</option>
                            {countries.map((c) => (
                                <option key={c.isoCode} value={c.isoCode}>
                                    {countryNameInFrench(c)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="pcWizField">
                        <label>{t("Ville")}</label>
                        <select
                            className="pcInput"
                            value={mission.ville}
                            onChange={(e) => setField("ville", e.target.value)}
                            disabled={!selectedCountryCode}
                        >
                            <option value="">{t("-- Choisir une ville --")}</option>
                            {cities.map((city, idx) => (
                                <option key={`${city.name}-${idx}`} value={city.name}>
                                    {city.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="pcWizField">
                        <label>{t("Adresse")}</label>
                        <input className="pcInput" value={mission.adresse} onChange={(e) => setField("adresse", e.target.value)} />
                    </div>
                </div>

                {/* Autres lignes */}
                <div className="pcWizField" style={{ width: "100%" }}>
                    <label>{t("Engagements du collaborateur")}</label>
                    <textarea
                        className="pcInput pcInput--textarea"
                        rows={5}
                        value={mission.engagementsCollaborateur}
                        onChange={(e) => setField("engagementsCollaborateur", e.target.value)}
                        placeholder={t("Saisir les engagements. Les retours à la ligne sont conservés.")}
                    />
                </div>

                {showWorkflow && (
                    <div className="pcWizField">
                        <label>{t("Workflow")}</label>
                        <select className="pcInput" value={mission.workflow} onChange={(e) => setField("workflow", e.target.value)}>
                            <option value="manager">{t("Validation Manager")}</option>
                            <option value="admin">{t("Validation RH")}</option>
                            <option value="both">{t("Manager + RH")}</option>
                        </select>
                    </div>
                )}
            </div>

            {showNotes && (
            <div className="pcWizField is-full">
                <label>{t("Notes de frais autorisées")}</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
                    {notesDeFrais.map((note) => {
                        const checked = mission.notesDeFraisAutoriseesIds.includes(note.id);
                        return (
                            <label key={note.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: 10, border: `1px solid ${checked ? "#3b82f6" : "#e2e8f0"}`, borderRadius: 8 }}>
                                <input type="checkbox" checked={checked} onChange={() => toggleNote(note.id)} />
                                <span>{(() => { const label = String(note.category || note.libelle || "").trim(); return t(label, { defaultValue: label }); })()}</span>
                            </label>
                        );
                    })}
                    {notesDeFrais.length === 0 && <span className="pcMuted">{t("Aucune note de frais disponible.")}</span>}
                </div>
            </div>
            )}

            {/* Justificatifs File Upload Area */}
            {showJustificatifs && (
            <div className="pcWizField is-full">
                <label>{t("Justificatifs / Pièces jointes")}</label>
                <input id={`${formId || "mission"}-files`} type="file" multiple onChange={handleFileChange} style={{ display: "none" }} />
                <label htmlFor={`${formId || "mission"}-files`} className="pcInput" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: "10px 12px" }}>
                    {t("Sélectionner des fichiers")}
                </label>
                {justificatifs.length > 0 && (
                    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                        {justificatifs.map((file, idx) => (
                            <div key={`${file.name}-${idx}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6 }}>
                                <span style={{ fontSize: "0.9rem", color: "#334155" }}>{file.name}</span>
                                <button
                                    type="button"
                                    onClick={() => removeFile(idx)}
                                    style={{
                                        border: "none",
                                        background: "transparent",
                                        color: "#ef4444",
                                        cursor: "pointer",
                                        fontSize: "0.85rem",
                                        fontWeight: "600",
                                    }}
                                >
                                    {t("Supprimer")}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            )}

            {showComment && (
            <div className="pcWizField is-full">
                <label>{t("Commentaire")}</label>
                <textarea className="pcInput pcInput--textarea" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />
            </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button type="submit" className="pcBtn pcBtn--primary" disabled={loading}>
                    {loading ? t("Envoi...") : submitLabel}
                </button>
            </div>
        </form>
    );
};

export default MissionInlineForm;
